<script>
import { fetchCompletedOperations } from '@domains/completed-operations/api'
import { fetchPosOperation } from '@domains/pos-operations/api'
import formModalPos from '@domains/pos-operations/components/modalPos/formModalPos.vue'
import appFilterSection from '@shared/components/shared/filterSection.vue'
import appTableSection from '@shared/components/shared/tableSection.vue'
import { useDynamicFilters, getCompletedOperationFilters } from '@shared/composables/useFilters'
import { useNoScroll } from '@shared/composables/useNoScroll'
import { usePaginatedResult } from '@shared/composables/usePaginatedResult'
import { useSearchThrottle } from '@shared/composables/useSearch'
import { handleGlobalError } from '@shared/services/errorHandler'
import { useHead } from '@unhead/vue'
import { onMounted, ref } from 'vue'

import router from '@/router'

export default {
  name: 'appFinished',

  components: {
    appFilterSection,
    appTableSection,
    formModalPos
  },

  props: {
    search: String,
    type: String,
    closedAt: String,
    id: String
  },

  setup(props) {
    useHead({ title: 'Finalizadas' })

    const searchValue = ref(props.search)
    const natureTypeValue = ref(props.type)
    const closingValue = ref(props.closedAt)
    const idEditOperation = ref(props.id)

    const valuesToWatch = [natureTypeValue, closingValue]

    useSearchThrottle(searchValue, () => {
      resetAndUpdateResult(true)
    })

    const { setNoScroll } = useNoScroll()

    const tableHeaders = [
      { key: 'clientName', label: 'Cliente', type: 'text' },
      {
        key: 'closedAt',
        label: 'Fechamento',
        type: 'date',
        format: 'dd/MM/yyyy HH:mm',
        timeZoneOffset: -3
      },
      {
        key: 'principalAmount',
        label: 'Valor',
        type: 'currency',
        locale: 'pt-BR',
        maximumFractionDigits: 2
      },
      { key: 'type', label: 'Tipo', type: 'operationType' },
      {
        key: 'commercialQuote',
        label: 'DOL COM',
        type: 'quotation',
        locale: 'pt-BR',
        maximumFractionDigits: 4
      },
      {
        key: 'clientQuote',
        label: 'Cotação do cliente',
        type: 'quotation',
        locale: 'pt-BR',
        maximumFractionDigits: 4
      },
      {
        key: 'actions',
        label: 'Ações',
        type: 'actions',
        options: [{ action: 'edit', label: 'Editar', class: 'edit', emitEvent: 'edit' }]
      }
    ]

    const fetchFunction = (config) => fetchCompletedOperations(config)

    const resetAndUpdateResult = (shouldReset) => {
      if (shouldReset) resetResult()

      const config = {
        page: currentPage.value,
        search: searchValue.value,
        type: natureTypeValue.value,
        closedAt: closingValue.value,
        id: idEditOperation.value
      }

      router.push({ name: 'CompletedOperations', query: { ...config, page: undefined } })
      fetchResult(config)
    }

    const {
      resultItems: operations,
      loading,
      isLastPage,
      fetchResult,
      resetResult,
      currentPage
    } = usePaginatedResult(fetchFunction, resetAndUpdateResult, valuesToWatch)

    onMounted(() => {
      if (idEditOperation.value) {
        const item = {
          id: idEditOperation.value
        }
        handleEdit(item)
      }
    })

    const title = ref('')
    const modalEnabled = ref(false)
    const initialFormData = ref({})

    const handleFormSubmit = async () => {
      resetAndUpdateResult(true)
      handleFormClose()
    }

    const handleFormClose = () => {
      initialFormData.value = {}
      modalEnabled.value = false
      setNoScroll(false)

      const { ...queryWithoutId } = router.currentRoute.value.query
      router.push({ name: 'CompletedOperations', query: queryWithoutId })
    }

    const handleEdit = async (item) => {
      try {
        const response = await fetchPosOperation(item.id)
        title.value = 'Editar Pós-operação'
        initialFormData.value = {
          ...response.data.payload
        }

        modalEnabled.value = true

        const config = {
          ...router.currentRoute.value.query,
          id: item.id
        }

        const queryParams = { ...config }
        router.push({ name: 'CompletedOperations', query: queryParams })
      } catch (e) {
        handleGlobalError(e)
      }
    }

    const filterSchema = getCompletedOperationFilters()
    const externalRefs = { searchValue, natureTypeValue, closingValue }
    const { formValues, updateFormValues } = useDynamicFilters(filterSchema, externalRefs)

    return {
      tableHeaders,
      operations,
      loading,

      isLastPage,

      modalEnabled,
      title,
      initialFormData,

      handleEdit,

      handleFormClose,
      handleFormSubmit,

      filterSchema,
      formValues,
      updateFormValues
    }
  }
}
</script>

<template>
  <div>
    <form-modal-pos
      v-if="modalEnabled"
      :title="title"
      @onClose="handleFormClose"
      :initialFormData="initialFormData"
      @onSubmitted="handleFormSubmit"
    />

    <div class="row title">
      <h2 class="col">Finalizadas</h2>
    </div>

    <app-filter-section
      :filterSchema="filterSchema"
      :formValues="formValues"
      @update:formValues="updateFormValues"
    />

    <app-table-section
      :items="operations"
      :headers="tableHeaders"
      :loading="loading"
      :isLastPage="isLastPage && !loading"
      :eventHandlers="{
        edit: handleEdit
      }"
    />
  </div>
</template>
